import { forEach } from 'lodash';

import CoreUtils from '../../common/utils/CoreUtils';

import { EnergyValueViewMode } from '../../common/value-types/EnergyValueViewMode';

import ScheduleDataModel from '../../schedules/ScheduleDataModel';

import BasicPropertyHelper from '../../schedules/property-helpers/BasicPropertyHelper';

import OptablePackageRuleRenderer from './OptablePackageRuleRenderer';

export default class OptableDataModel extends ScheduleDataModel {
  constructor(locale, counterparty, commandDataModel, optedDataField, optedDataValue) {
    super(locale, counterparty, commandDataModel)

    this.optedDataField = optedDataField;
    this.optedDataValue = optedDataValue;
  }

  getDefaultSortCriteria() {
    return {
      fields: ['localDateMs', 'contractCode'],
      order: ['asc', 'asc']
    };
  }

  processContracts(scheduleUpdateDto) {
    forEach(scheduleUpdateDto.contracts, contractDto => {
      var contract = {};
      Object.assign(contract, contractDto);

      this.processOptableContract(contractDto, contract);
      this.registerContract(contract);
    });
  }

  processOptableContract(contractDto, contract) {
    contract.enforceBinaryElection = contractDto.parameters.EnforceBinaryElection === '1';
    contract.showOptedOutEnergyValues = contractDto.parameters.ShowOptedOutEnergyValues === '1';
  }

  onScheduleCreating(schedule, contract) {
    schedule.enforceBinaryElection = contract.enforceBinaryElection;
    schedule.showOptedOutEnergyValues = contract.showOptedOutEnergyValues;
  }

  onScheduleUpdated(schedule, isServerUpdate, isPeerUpdate, updatedDataFields, quickEntryHints) {
    super.onScheduleUpdated(schedule, isServerUpdate, isPeerUpdate, updatedDataFields, quickEntryHints);

    if (!isServerUpdate && !isPeerUpdate)
      this.enforceSchedulePackageValue(schedule, this.optedDataField, updatedDataFields, quickEntryHints);
  }

  onSchedulePropertiesCreating(schedule, contract, scheduleDto) {
    super.onSchedulePropertiesCreating(schedule, contract, scheduleDto);

    BasicPropertyHelper.onPropertyCreating(
      schedule,
      this.optedDataField,
      '',
      scheduleDto.metaValues.isOptedIn === this.optedDataValue,
      scheduleDto.firstUtcElectionCutoff
    );

    this.updateOptableValueViewMode(schedule);
  }

  onSchedulePropertiesServerUpdate(schedule, contract, scheduleDto, isPeerUpdate, updatedDataFields) {
    super.onSchedulePropertiesServerUpdate(schedule, contract, scheduleDto, isPeerUpdate, updatedDataFields);

    BasicPropertyHelper.onPropertyServerUpdate(
      schedule,
      this.optedDataField,
      scheduleDto.metaValues.isOptedIn === this.optedDataValue,
      true,
      isPeerUpdate,
      updatedDataFields,
      this.locale
    );

    this.updateOptableValueViewMode(schedule);
  }

  onSchedulePropertiesModifying(schedule, contract, scheduleUpdateSet, updatedDataFields) {
    super.onSchedulePropertiesModifying(schedule, contract, scheduleUpdateSet, updatedDataFields);

    BasicPropertyHelper.onPropertyModifying(
      schedule,
      this.optedDataField,
      schedule[this.optedDataField],
      scheduleUpdateSet,
      updatedDataFields,
      this.locale
    );

    this.updateOptableValueViewMode(schedule);
  }

  onSchedulePropertiesEditing(schedule, dataFieldHints, updatedDataFields) {
    super.onSchedulePropertiesEditing(schedule, dataFieldHints, updatedDataFields);

    BasicPropertyHelper.onPropertyEditing(
      schedule,
      this.optedDataField,
      dataFieldHints,
      updatedDataFields
    );

    this.updateOptableValueViewMode(schedule);
  }

  onSchedulePropertiesCutoffCheck(schedule, serverUtcTimeMs, warningPeriodMs, updatedDataFields) {
    super.onSchedulePropertiesCutoffCheck(schedule, serverUtcTimeMs, warningPeriodMs, updatedDataFields);

    BasicPropertyHelper.onPropertyCutoffCheck(
      schedule,
      this.optedDataField,
      updatedDataFields,
      serverUtcTimeMs,
      warningPeriodMs
    );

    this.updateOptableValueViewMode(schedule);
  }

  onSchedulePropertiesReverting(schedule, updatedDataFields) {
    super.onSchedulePropertiesReverting(schedule, updatedDataFields);

    BasicPropertyHelper.onPropertyReverting(
      schedule,
      this.optedDataField,
      updatedDataFields
    );

    this.updateOptableValueViewMode(schedule);
  }

  onSchedulePropertiesSerialize(schedule, scheduleSubmissionDto) {
    super.onSchedulePropertiesSerialize(schedule, scheduleSubmissionDto);

    scheduleSubmissionDto.metaValues.isOptedIn = schedule[this.optedDataField] === this.optedDataValue;
  }

  onSchedulesInitialized(schedules) {
    super.onSchedulesInitialized(schedules);

    schedules.forEach(
      schedule => this.determinePackageRuleFlags(
        schedule,
        this.optedDataField,
        (packageRule, otherDates) => OptablePackageRuleRenderer(
          new Date(schedule.scheduleDate),
          packageRule.canConfigureIndependently,
          otherDates,
          this.optedDataValue ? 'opted in' : 'opted out',
          this.locale
        )
      )
    );
  }

  updateOptableValueViewMode(schedule) {
    schedule.isGrayedOut = schedule[this.optedDataField] !== this.optedDataValue;
    schedule.energyValueViewMode = schedule[this.optedDataField] === this.optedDataValue || !schedule.showOptedOutEnergyValues
      ? EnergyValueViewMode.energyValue
      : EnergyValueViewMode.referenceValue;
  }

  isElectableHourField(schedule, dataField) {
    if (super.isElectableHourField(schedule, dataField))
      return true;

    return dataField === this.optedDataField && schedule.enableEditValues;
  }

  canEditFieldValue(schedule, dataField) {
    return schedule.hourlyDataFields.indexOf(dataField) === -1
        || (!schedule.enforceBinaryElection && schedule[this.optedDataField] === this.optedDataValue)
      ? super.canEditFieldValue(schedule, dataField)
      : false;
  }

  onFieldValuesEditing(schedule, dataFieldValues) {
    if (!super.onFieldValuesEditing(schedule, dataFieldValues))
      return false;

    return this.filterUpdatingElectableHourFields(
      schedule,
      dataFieldValues,
      this.indexedContracts[schedule.contractCode]
    );
  }

  isHourQuickEntryModifiable(schedule, dataField, hourTypeCode) {
    var contract = this.indexedContracts[schedule.contractCode];

    return schedule[this.optedDataField] === this.optedDataValue
      && !contract.enforceBinaryElection
      && super.isHourQuickEntryModifiable(schedule, dataField, hourTypeCode);
  }

  updateScheduleUsage(schedule, updatedDataFields) {
    super.updateScheduleUsage(schedule, updatedDataFields);

    var energySumMWh = 0;
    var energySumH = 0;

    forEach(
      schedule.hourlyDataFields, dataField => {
        energySumMWh += schedule[dataField].averageValue;
        energySumH += schedule[dataField].averageDuration;
      }
    );

    if (energySumMWh !== schedule.energySumMWh) {
      schedule.energySumMWh = energySumMWh;
      updatedDataFields.push('energySumMWh');
    }

    if (energySumH !== schedule.energySumH) {
      schedule.energySumH = energySumH;
      updatedDataFields.push('energySumH');
    }
  }

  establishEditFieldTriggers(editFieldTriggers) {
    super.establishEditFieldTriggers(editFieldTriggers);

    editFieldTriggers.push({
      triggerDataField: this.optedDataField,
      triggerValue: false,
      updateDataField: ScheduleDataModel.allElectableHourFields,
      updateValue: this.optedDataValue
        ? ScheduleDataModel.minimumValue
        : ScheduleDataModel.referenceValue
    });

    editFieldTriggers.push({
      triggerDataField: this.optedDataField,
      triggerValue: true,
      updateDataField: ScheduleDataModel.allElectableHourFields,
      updateValue: this.optedDataValue
        ? ScheduleDataModel.referenceValue
        : ScheduleDataModel.minimumValue
    });
  }

  enforceSchedulePackageValue(schedule, dataField, updatedDataFields, quickEntryHints) {
    if (updatedDataFields.indexOf(dataField) === -1)
      return;

    var dateKey = ScheduleDataModel.generateDateKey(schedule.scheduleDate);
    var packageRule = this.indexedPackageRules[schedule.contractCode][dateKey];

    if (CoreUtils.isUndefinedOrNull(packageRule))
      return;

    if ((schedule[dataField] && packageRule.canConfigureIndependently))
      return;

    var scheduleUpdateSets = [];
    forEach(packageRule.packageDateKeys, packageDateKey => {
      var scheduleDateKey = ScheduleDataModel.generateScheduleDateKey(
        packageDateKey,
        schedule.contractCode
      );

      var packageSchedule = this.dateIndexedSchedules[scheduleDateKey];
      if (CoreUtils.isDefinedAndNotNull(packageSchedule) && packageSchedule.scheduleId !== schedule.scheduleId) {
        if (packageSchedule[dataField] !== schedule[dataField])
          scheduleUpdateSets.push(
            this.generateSingleValueScheduleUpdateSet(
              packageSchedule.scheduleId,
              [dataField],
              schedule[dataField]
            )
          );
      }
    });

    if (scheduleUpdateSets.length > 0)
      this.updateSchedules(scheduleUpdateSets, false);
  }
}